import { cleanInput } from '@exodus/basic-utils';
import type NumberUnit from '@exodus/currency';
import { formatAssetAmount } from '@exodus/formatting/lib/asset';
import TextField from '@passkeys/ui/text-field';
import { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';

import type { Asset } from '@/types';
import formatFiatSelector from '@/ui/flux/fiat/selectors/format-fiat';
import selectors from '@/ui/flux/selectors';
import useConversion from '@/ui/hooks/use-conversion';
import { cn } from '@/ui/utils/classnames';
import CurrencySelector from './currency-selector.js';

interface AmountInputProps {
  asset: Asset;
  onAmountChange: (event: { crypto: NumberUnit; fiat: NumberUnit }) => void;
  onModeChange: (mode: string) => void;
  onAllClick: () => void;
  amount?: NumberUnit;
  className?: string;
  dataTestId?: string;
}

function AmountInput({
  className,
  asset,
  amount,
  onAllClick,
  onAmountChange,
  onModeChange,
  dataTestId,
}: AmountInputProps) {
  const ref = useRef(null);

  const [value, setValue] = useState('');

  const [fiatMode, setFiatMode] = useState(false);

  const fiatConversion = useConversion(asset.name);

  const formatFiat = useSelector(formatFiatSelector);

  const configFiat = useSelector(selectors.locale.currencyUnitType) as any;

  const decimals = fiatMode ? null : asset.currency.defaultUnit.power;

  const updateValue = useCallback(
    (amount) => {
      if (!amount) {
        return;
      }

      if (fiatMode) {
        setValue(formatFiat(fiatConversion(amount)));
      } else {
        setValue(formatAssetAmount(amount));
      }
    },
    [fiatConversion, fiatMode, formatFiat],
  );

  useEffect(() => {
    const isFocused = document.activeElement === ref.current;

    if (!isFocused) {
      updateValue(amount);
    }
  }, [amount, updateValue]);

  const handleFiatModeChange = (fiatMode: boolean) => {
    updateValue(amount);
    setFiatMode(fiatMode);
    onModeChange(fiatMode ? 'fiat' : 'crypto');
  };

  const handleChange = (value) => {
    try {
      const newValue = cleanInput(value, decimals);
      setValue(`${fiatMode ? '$' : ''}${newValue}`);

      if (fiatMode) {
        const numberUnit = configFiat.defaultUnit(newValue);
        onAmountChange({
          crypto: fiatConversion(numberUnit),
          fiat: numberUnit,
        });
      } else {
        const numberUnit = asset.currency.defaultUnit(newValue);
        onAmountChange({
          crypto: numberUnit,
          fiat: fiatConversion(numberUnit),
        });
      }
    } catch {}
  };

  const actions = (
    <CurrencySelector
      asset={asset}
      fiatMode={fiatMode}
      configFiat={configFiat}
      onAllClick={onAllClick}
      onChange={handleFiatModeChange}
    />
  );

  return (
    <TextField
      ref={ref}
      className={cn(
        // TODO: Clean up so that the consumer doesn't have to know about `after`.
        'bg-white after:bg-white theme-dark:bg-neutral-600 theme-dark:after:bg-neutral-600',
        className,
      )}
      value={value}
      placeholder="0.00"
      inputMode="decimal"
      suffix={actions}
      dataTestId={dataTestId}
      onChange={handleChange}
    />
  );
}

export default AmountInput;
